import { isNativePlatform } from './nativeSocialAuth';

/**
 * The canonical public origin. Used for canonical/OG URLs in PageMeta, and as the base for
 * the legal documents the native app links out to.
 */
export const SITE_URL = 'https://agence.finance';

/**
 * The parent studio's site, linked from the copyright credit. Exact URL, no query string:
 * it exists to give kalotech.dev a crawlable inbound link, and a UTM-tagged variant would
 * split that signal across URLs.
 */
export const KALOTECH_URL = 'https://kalotech.dev';

/**
 * Paths whose text is served from the web deploy rather than bundled in the native binary.
 *
 * Decided 2026-09-01: Privacy, Terms and the Automation Risk Disclosure are all still under
 * attorney review (C1-C4). Bundling them would turn every redline into an App Review
 * resubmission, and would let the shipped copy drift from the policy the store listing's own
 * Privacy Policy URL points at. Web-hosted-and-linked is also the documented common practice
 * for app legal documents; the usual caveat — keep an offline in-app copy — applies to apps
 * that process user data offline, which Agence cannot do, since Plaid, Alpaca and chat all
 * require the network.
 *
 * Help & FAQ is deliberately absent: it carries no legal weight, so there is no redline
 * problem to solve, and sending a user to a browser for support content is worse than
 * keeping them in the app.
 */
export const LEGAL_LINKS = [
  { label: 'Privacy Policy', path: '/privacy' },
  { label: 'Terms of Service', path: '/terms' },
  { label: 'Automation Risk Disclosure', path: '/automation-disclosure' },
];

/**
 * Open a page on the public site from inside the app.
 *
 * Agence bundles its web assets, so the native WebView's origin is `capacitor://localhost`.
 * A plain navigation to an external origin there either gets blocked or replaces the app's
 * own document with the remote site — and a Capacitor WebView has no browser chrome, so the
 * user would be stranded with no back button. `window.open(url, '_system')` hands it to the
 * real browser instead. On the web there is no WebView to escape, so this is an ordinary new
 * tab with `noopener` (never `_system`, which would just create an oddly-named window).
 *
 * Returns true when it handled the navigation, so callers can `preventDefault()` on the
 * anchor only in that case and leave normal web link behaviour alone.
 */
export function openSitePage(url) {
  if (isNativePlatform()) {
    window.open(url, '_system');
    return true;
  }
  window.open(url, '_blank', 'noopener');
  return true;
}
